import type { CrowdexSavedAgent, CrowdexUIMessage } from '@crowdex/agent-contracts';
import { useEffect, useState } from 'react';
import { readAccountAgentProfiles } from '@/lib/agent-profiles-client';
import type { MentionTarget } from '@/lib/side-panel/mention-targets';
import { personaMentionOf } from './persona-reply-client';
import { personaSharesRuntime } from './persona-runtime';

let cached: readonly CrowdexSavedAgent[] = [];
let loading: Promise<readonly CrowdexSavedAgent[]> | null = null;

/** The persona a user's message mentions (`@[Designer](persona:designer)`), if any. */
export function personaMentionIn(
  message: CrowdexUIMessage | undefined,
): ReturnType<typeof personaMentionOf> {
  if (message?.role !== 'user') return null;
  const text = message.parts.flatMap((part) => (part.type === 'text' ? [part.text] : [])).join('');
  return personaMentionOf(text);
}

/**
 * The persona a message brings in for its reply (`@[Designer](persona:designer)`), when it runs
 * on `agentId`'s runtime and can take the conversation's turn; null otherwise.
 */
export function personaTurnOf(
  message: CrowdexUIMessage | undefined,
  agentId: string,
  personas: readonly CrowdexSavedAgent[],
): CrowdexSavedAgent | null {
  const mention = personaMentionIn(message);
  const persona = mention === null ? undefined : personas.find((entry) => entry.id === mention.id);
  return persona !== undefined && personaSharesRuntime(persona.agentId, agentId) ? persona : null;
}

/**
 * The saved agents once read: the read in flight, or the first one. What runs where is decided
 * on this, never on the empty cache of a cold start.
 */
export function readAgentPersonas(): Promise<readonly CrowdexSavedAgent[]> {
  return loading ?? refreshAgentPersonas();
}

/** Reads the account's saved agents again (after they changed). */
function refreshAgentPersonas(): Promise<readonly CrowdexSavedAgent[]> {
  loading = readAccountAgentProfiles()
    .then((profiles) => {
      cached = profiles;
      return profiles;
    })
    .catch(() => cached);
  return loading;
}

/**
 * The personas `@` can bring into a conversation for one reply (design D51): the account's
 * saved agent profiles (`/api/agent-profiles`), read once and shared.
 */
export function useAgentPersonas(enabled = true): readonly CrowdexSavedAgent[] {
  const [personas, setPersonas] = useState(cached);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    void (loading ?? refreshAgentPersonas()).then((profiles) => {
      if (live) setPersonas(profiles);
    });
    return () => {
      live = false;
    };
  }, [enabled]);
  return personas;
}

/** A persona as a target of the composer's `@` menu ("Personas"). */
export function personaMentionTarget(persona: CrowdexSavedAgent): MentionTarget {
  return {
    id: persona.id,
    kind: 'persona',
    label: persona.name,
    detail: persona.description ?? 'Replies once in this conversation',
  };
}
